import { applyHandResults, createSession, type SessionState } from '@rummy/engine';
import type { AccountId, HandResult, SessionView } from '@rummy/protocol';

/**
 * Everything the end-of-session screen says about a session, derived once from
 * the two things the server actually sends: final standings and the hand-by-hand
 * history (§4.5).
 *
 * This is a pure function with its own tests rather than arithmetic scattered
 * through JSX, because "hands won" and "average per hand" are claims about the
 * session and a wrong one is worse than no stat at all.
 */

export type AwardKey = 'wins' | 'steady' | 'tight';

export interface Award {
  key: AwardKey;
  player: AccountId;
  value: number;
}

export interface PlayerStats {
  id: AccountId;
  /** Authoritative final score, straight from standings. */
  score: number;
  /** 1-based; players on equal scores share a rank. */
  rank: number;
  eliminated: boolean;
  eliminatedAtHand: number | null;
  /** §3.8 — the one thing that makes the hand column stop adding up to the
   *  total, so the screen has to be able to say it happened. */
  reentry: { hand: number; from: number; to: number } | null;
  handsPlayed: number;
  handsWon: number;
  drops: number;
  invalid: number;
  /** Fewest points conceded in any hand, wins included. */
  best: number | null;
  worst: number | null;
  average: number | null;
  /** Fewest points conceded in a hand that was played out and lost — how close
   *  they came, which a drop does not tell you. */
  tightest: number | null;
  /** Score after each hand, starting with 0 before the first. */
  series: number[];
  /** This player's result in each hand, `null` where they sat one out. */
  perHand: (HandResult | null)[];
}

export interface SessionStats {
  hands: number;
  /** Ranked: lowest score first, which is the winner. */
  players: PlayerStats[];
  /** Highest point any line reaches, for scaling the chart. */
  peak: number;
  /** Points between the winner and the runner-up, or `null` when tied. */
  margin: number | null;
  awards: Award[];
}

const DROP_OUTCOMES = new Set(['first-drop', 'middle-drop', 'auto-drop']);

export function computeSessionStats(
  session: SessionView,
  seatIds: readonly AccountId[],
): SessionStats {
  const history = [...session.history].sort((a, b) => a.ordinal - b.ordinal);
  const series = replayScores(session, seatIds, history);

  const players: PlayerStats[] = seatIds.map((id) => {
    const perHand = history.map((entry) => entry.results.find((r) => r.player === id) ?? null);
    const played = perHand.filter((r): r is HandResult => r !== null);
    const points = played.map((r) => r.points);
    const lost = played.filter((r) => r.outcome === 'lost').map((r) => r.points);
    const standing = session.standings[id];
    return {
      id,
      score: standing?.score ?? 0,
      rank: 0,
      eliminated: standing?.eliminated ?? false,
      eliminatedAtHand: standing?.eliminatedAtHand ?? null,
      reentry: standing?.reentry ?? null,
      handsPlayed: played.length,
      handsWon: played.filter((r) => r.outcome === 'won').length,
      drops: played.filter((r) => DROP_OUTCOMES.has(r.outcome)).length,
      invalid: played.filter((r) => r.outcome === 'invalid-declaration').length,
      best: points.length > 0 ? Math.min(...points) : null,
      worst: points.length > 0 ? Math.max(...points) : null,
      average: points.length > 0 ? points.reduce((a, b) => a + b, 0) / points.length : null,
      tightest: lost.length > 0 ? Math.min(...lost) : null,
      series: series.get(id) ?? [0],
      perHand,
    };
  });

  players.sort((a, b) => a.score - b.score || a.id.localeCompare(b.id));
  let rank = 0;
  let previous: number | null = null;
  players.forEach((p, i) => {
    if (previous === null || p.score !== previous) rank = i + 1;
    previous = p.score;
    p.rank = rank;
  });

  const peak = Math.max(1, ...players.flatMap((p) => p.series));
  const margin =
    players.length > 1 && players[1]!.score > players[0]!.score
      ? players[1]!.score - players[0]!.score
      : null;

  return { hands: history.length, players, peak, margin, awards: awardsFor(players) };
}

/**
 * Pool sessions do not accumulate: elimination freezes a score and a re-entry
 * resets it to the field's highest (§3.8). Summing the history column would
 * therefore print a total that disagrees with the standings the server sent, so
 * the trajectory is replayed through the engine's own rule instead of a second
 * implementation of it here.
 *
 * The view now carries the room's re-entry setting, so the replay uses it. The
 * alternatives are still tried behind it, because a session recorded before the
 * setting was in the view has to land somewhere sensible rather than draw a
 * chart that contradicts the scoreboard beside it.
 */
function replayScores(
  session: SessionView,
  seatIds: readonly AccountId[],
  history: SessionView['history'],
): Map<AccountId, number[]> {
  let fallback: Map<AccountId, number[]> | null = null;

  for (const reentryEnabled of [session.reentryEnabled, undefined, true, false]) {
    let state: SessionState = createSession(
      session.format,
      seatIds,
      reentryEnabled === undefined ? {} : { reentryEnabled },
    );
    const series = new Map<AccountId, number[]>(seatIds.map((id) => [id, [0]]));
    for (const entry of history) {
      state = applyHandResults(state, entry.ordinal, entry.results);
      for (const id of seatIds) series.get(id)!.push(state.standings[id]?.score ?? 0);
    }

    const matches = seatIds.every(
      (id) => (state.standings[id]?.score ?? 0) === (session.standings[id]?.score ?? 0),
    );
    if (matches) return series;
    fallback ??= series;
  }

  // Neither setting reproduced the totals — an unexpected divergence. Show the
  // shape we have, but land on the authoritative number so the chart and the
  // scoreboard never contradict each other.
  for (const id of seatIds) {
    const line = fallback!.get(id)!;
    line[line.length - 1] = session.standings[id]?.score ?? 0;
  }
  return fallback!;
}

/**
 * Three honest superlatives, awarded only when the session actually earned
 * them. A trophy for "most wins" in a session where nobody declared would be
 * decoration pretending to be information.
 */
function awardsFor(players: readonly PlayerStats[]): Award[] {
  if (players.length < 2) return [];
  const awards: Award[] = [];

  const best = (
    key: AwardKey,
    value: (p: PlayerStats) => number | null,
    better: (a: number, b: number) => boolean,
    qualifies: (v: number) => boolean = () => true,
  ): void => {
    let winner: PlayerStats | null = null;
    let winning = 0;
    for (const p of players) {
      const v = value(p);
      if (v === null || !qualifies(v)) continue;
      if (winner === null || better(v, winning)) {
        winner = p;
        winning = v;
      }
    }
    // Players are already ranked, so the first qualifier on a tie is the one
    // with the better session — which is the tiebreak a table would use.
    if (winner) awards.push({ key, player: winner.id, value: winning });
  };

  best('wins', (p) => p.handsWon, (a, b) => a > b, (v) => v > 0);
  best('steady', (p) => (p.handsPlayed >= 2 ? p.average : null), (a, b) => a < b);
  best('tight', (p) => p.tightest, (a, b) => a < b);

  return awards;
}
